import { mergeAttributes, Node } from "@tiptap/core";

import { EMBED_IFRAME_HOSTS, type EmbedProvider, toEmbedTarget } from "./embed-url";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    embed: {
      /** Insert a YouTube/Loom embed from a pasted link. Returns false for unsupported links. */
      setEmbed: (url: string) => ReturnType;
    };
  }
}

const isAllowedSrc = (src: unknown): src is string => {
  if (typeof src !== "string") return false;
  try {
    const url = new URL(src);
    return (
      url.protocol === "https:" && (EMBED_IFRAME_HOSTS as readonly string[]).includes(url.host)
    );
  } catch {
    return false;
  }
};

/** A responsive video embed (YouTube via youtube-nocookie, or Loom). */
export const Embed = Node.create({
  name: "embed",
  group: "block",
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      provider: { default: "youtube" as EmbedProvider },
      src: { default: null as string | null },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div[data-embed]",
        getAttrs: (element) => {
          const iframe = (element as HTMLElement).querySelector("iframe");
          const src = iframe?.getAttribute("src");
          if (!isAllowedSrc(src)) return false;
          return { provider: (element as HTMLElement).getAttribute("data-embed"), src };
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const src = isAllowedSrc(node.attrs["src"]) ? node.attrs["src"] : "about:blank";
    return [
      "div",
      mergeAttributes({ "data-embed": node.attrs["provider"], class: "cms-embed" }),
      [
        "iframe",
        mergeAttributes(
          {
            src,
            title: `${node.attrs["provider"] === "loom" ? "Loom" : "YouTube"} video`,
            loading: "lazy",
            allow: "autoplay; encrypted-media; picture-in-picture; fullscreen",
            allowfullscreen: "true",
            referrerpolicy: "strict-origin-when-cross-origin",
          },
          // Never let stored attributes override the iframe's own.
          Object.fromEntries(Object.entries(HTMLAttributes).filter(([key]) => key === "class")),
        ),
      ],
    ];
  },

  addCommands() {
    return {
      setEmbed:
        (url) =>
        ({ commands }) => {
          const target = toEmbedTarget(url);
          if (!target) return false;
          return commands.insertContent({ type: this.name, attrs: target });
        },
    };
  },
});
